/**
 * [C-10] CameraCapture — mockup หน้าจอ 1–2 · PRD F-10 F-11
 *
 * - เปิด stream ทันที facingMode environment · ไฟฉายปิดเป็นค่าตั้งต้น (มีปุ่มเสมอ)
 * - แถบล่างสามช่อง 1fr auto 1fr: คลังภาพ · ชัตเตอร์ 66px · ไฟฉาย — **ชัตเตอร์อยู่กลางทุกสถานะ ไม่มีสถานะไหนซ่อนหรือ disable**
 * - ถอด QR จากเฟรมสดด้วย `decodeSlipQr` ของ slip-qr (ไม่เขียนตัวถอดใหม่) พบแล้ว → วงแหวนรอบชัตเตอร์ 1 วินาที → ถ่ายเอง · กดชัตเตอร์แทรกได้
 * - ชิป "อัตโนมัติ เปิด/ปิด" มุมขวาบน · จำค่าต่ออุปกรณ์ (localStorage — เป็นค่าตั้งค่า ไม่ใช่สถานะสำคัญ)
 * - ปฏิเสธสิทธิ์กล้อง / ไม่มีกล้อง → Alert ในหน้า (ไม่ใช่ป๊อปอัป) บอกวิธีเปิดสิทธิ์ · แถบล่างเดิม คลังภาพยังใช้ได้ → ยังบันทึกได้ (กฎเหล็กข้อ 1)
 * - ทุกภาพ (ถ่ายเอง/อัตโนมัติ/คลัง) → prepareImage (≤1568px · JPEG .85 · sha256) → ถอด QR จากภาพนิ่ง → onCapture
 *
 * render = html string (ทดสอบได้ใน Node) · mount = ผูกกล้อง/ลูปถอด/ปุ่ม
 */
import { decodeSlipQr, type QrDecodeResult } from "@pidyod/slip-qr";
import { html } from "../h.js";
import { frameImageData, prepareImage, type PreparedImage } from "../image.js";
import { Alert } from "./basics.js";

export const AUTO_KEY = "pidyod.autoCapture";
export const AUTO_DELAY_MS = 1000;
export const LIVE_INTERVAL_MS = 300;

export type CameraPhase = "starting" | "live" | "found" | "denied" | "capturing";

export interface CameraState {
  shopName: string;
  staffName: string;
  autoCapture: boolean;
  torch: boolean;
  torchAvailable: boolean;
  phase: CameraPhase;
}

export interface CaptureEvent {
  image: PreparedImage;
  qr: QrDecodeResult;
  source: "auto" | "shutter" | "gallery";
}

/** ข้อความช่วยตาม mockup — ไม่เกิน 20 คำ */
const HINT_DEFAULT = html`<b>วางสลิปให้เต็มกรอบ</b>เจอ QR แล้วระบบถ่ายให้เอง หรือกดถ่ายเองได้เลย`;
const HINT_FOUND = html`<span class="cam-found"><span class="dot"></span>พบ QR — ถ่ายให้ใน 1 วินาที</span><br>กดชัตเตอร์เพื่อถ่ายทันที`;
const HINT_MANUAL = html`<b>วางสลิปให้เต็มกรอบ</b>กดชัตเตอร์เพื่อถ่าย`;

export function renderCamera(s: CameraState): string {
  const found = s.phase === "found" || s.phase === "capturing";
  return html`
    <div class="cam" data-phase="${s.phase}">
      <video autoplay playsinline muted></video>
      <div class="cam-top">
        <button type="button" class="chip chip-switch" data-action="switch-shop" aria-label="สลับร้าน">${s.shopName}</button>
        <button type="button" class="chip chip-auto${s.autoCapture ? "" : " off"}" data-action="toggle-auto" aria-pressed="${String(s.autoCapture)}">อัตโนมัติ <b>${s.autoCapture ? "เปิด" : "ปิด"}</b></button>
      </div>
      ${s.phase === "denied" ? html`<div class="cam-alert">${Alert({ tone: "danger", title: "เปิดกล้องไม่ได้", body: "ไปที่ ตั้งค่า › LINE › กล้อง แล้วเปิดสิทธิ์ · หรือเลือกภาพจากคลังด้านล่างได้เลย", action: { label: "ลองเปิดกล้องอีกครั้ง", action: "retry-camera" } })}</div>` : null}
      <div class="cam-frame"><i></i><i></i><i></i><i></i>${found ? html`<div class="cam-qr"></div>` : html`<div class="cam-scan"></div>`}</div>
      <div class="cam-hint">${found ? HINT_FOUND : s.autoCapture ? HINT_DEFAULT : HINT_MANUAL}</div>
      <div class="dock">
        <button type="button" class="dock-side" data-action="gallery"><i class="ic">▤</i><small>คลังภาพ</small></button>
        <button type="button" class="shutter${found ? " ring" : ""}" data-action="shutter" aria-label="ถ่าย"></button>
        <button type="button" class="dock-side${s.torch ? " on" : ""}" data-action="torch" aria-pressed="${String(s.torch)}"><i class="ic">⚡</i><small>ไฟฉาย</small></button>
      </div>
      <input type="file" accept="image/*" class="hidden" data-role="gallery-input">
    </div>`.s;
}

export function readAutoPref(storage: Pick<Storage, "getItem"> = localStorage): boolean {
  try { return storage.getItem(AUTO_KEY) !== "off"; } catch { return true; }
}

export interface CameraHandle {
  stop(): void;
  state: CameraState;
}

/** ผูกพฤติกรรมกับ DOM ที่ render แล้ว — root คือ element ที่มี .cam อยู่ข้างใน */
export function mountCamera(
  root: HTMLElement,
  init: { shopName: string; staffName: string },
  on: { capture: (e: CaptureEvent) => void; switchShop?: () => void },
): CameraHandle {
  const state: CameraState = { shopName: init.shopName, staffName: init.staffName, autoCapture: readAutoPref(), torch: false, torchAvailable: false, phase: "starting" };
  let stream: MediaStream | null = null;
  let timer: ReturnType<typeof setInterval> | null = null;
  let autoTimer: ReturnType<typeof setTimeout> | null = null;
  let busy = false;
  const work = document.createElement("canvas");

  const video = (): HTMLVideoElement => root.querySelector("video")!;

  function render(): void {
    const old = video();
    root.innerHTML = renderCamera(state);
    // video element ใหม่รับ stream เดิม — ไม่ต้องขอสิทธิ์ซ้ำ
    if (stream) { video().srcObject = stream; void video().play().catch(() => {}); }
    else if (old && old.srcObject) { video().srcObject = old.srcObject; }
  }

  async function start(): Promise<void> {
    state.phase = "starting"; render();
    try {
      stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: { ideal: "environment" }, width: { ideal: 1920 }, height: { ideal: 1080 } }, audio: false });
    } catch {
      state.phase = "denied"; render(); return;
    }
    const track = stream.getVideoTracks()[0];
    const caps = (track?.getCapabilities?.() ?? {}) as MediaTrackCapabilities & { torch?: boolean };
    state.torchAvailable = caps.torch === true;
    state.phase = "live"; render();
    if (timer) clearInterval(timer);
    timer = setInterval(() => void tick(), LIVE_INTERVAL_MS);
  }

  /** ลูปถอด QR จากเฟรมสด — พบแล้วเริ่มนับ 1 วิ (วงแหวน) แล้วถ่ายเอง */
  async function tick(): Promise<void> {
    if (!state.autoCapture || state.phase !== "live" || busy) return;
    const v = video();
    const img = frameImageData(v, work);
    if (!img) return;
    busy = true;
    try {
      const qr = await decodeSlipQr(img, { deep: false });
      if (qr.qr_ok && state.phase === "live") {
        state.phase = "found"; render();
        autoTimer = setTimeout(() => void capture("auto"), AUTO_DELAY_MS);
      }
    } finally { busy = false; }
  }

  async function capture(source: "auto" | "shutter"): Promise<void> {
    if (autoTimer) { clearTimeout(autoTimer); autoTimer = null; }
    const v = video();
    if (!stream || v.videoWidth === 0) return; // ไม่มีภาพสด (สิทธิ์ถูกปฏิเสธ) — ใช้คลังภาพ
    state.phase = "capturing"; render();
    const image = await prepareImage(v, v.videoWidth, v.videoHeight);
    const qr = await decodeSlipQr(image.imageData);
    on.capture({ image, qr, source });
  }

  async function fromFile(file: File): Promise<void> {
    let bitmap: ImageBitmap;
    try { bitmap = await createImageBitmap(file); }
    catch {
      // ไฟล์ที่เปิดไม่ได้ (HEIC บนบางเครื่อง) — บอกวิธีแก้ในหน้า ไม่ใช่ป๊อปอัป
      root.querySelector(".cam-top")!.insertAdjacentHTML("afterend", `<div class="cam-alert">${Alert({ tone: "warn", title: "เปิดไฟล์นี้ไม่ได้", body: "ถ้าเป็น HEIC ให้แปลงเป็น JPG ก่อน หรือถ่ายจากกล้องแทน" }).s}</div>`);
      return;
    }
    const image = await prepareImage(bitmap, bitmap.width, bitmap.height);
    bitmap.close();
    on.capture({ image, qr: await decodeSlipQr(image.imageData), source: "gallery" });
  }

  async function toggleTorch(): Promise<void> {
    const track = stream?.getVideoTracks()[0];
    if (!track) return;
    state.torch = !state.torch;
    try { await track.applyConstraints({ advanced: [{ torch: state.torch } as MediaTrackConstraintSet] }); }
    catch { state.torch = false; }
    render();
  }

  root.addEventListener("click", (ev) => {
    const btn = (ev.target as HTMLElement).closest<HTMLElement>("[data-action]");
    if (!btn) return;
    switch (btn.dataset["action"]) {
      case "shutter": void capture("shutter"); break;
      case "gallery": root.querySelector<HTMLInputElement>("[data-role=gallery-input]")!.click(); break;
      case "torch": void toggleTorch(); break;
      case "toggle-auto":
        state.autoCapture = !state.autoCapture;
        try { localStorage.setItem(AUTO_KEY, state.autoCapture ? "on" : "off"); } catch { /* private mode */ }
        if (!state.autoCapture && autoTimer) { clearTimeout(autoTimer); autoTimer = null; state.phase = "live"; }
        render();
        break;
      case "retry-camera": void start(); break;
      case "switch-shop": on.switchShop?.(); break;
    }
  });
  root.addEventListener("change", (ev) => {
    const input = ev.target as HTMLInputElement;
    if (input.dataset["role"] === "gallery-input" && input.files?.[0]) { void fromFile(input.files[0]); input.value = ""; }
  });

  void start();
  return {
    state,
    stop() {
      if (timer) clearInterval(timer);
      if (autoTimer) clearTimeout(autoTimer);
      stream?.getTracks().forEach((t) => t.stop());
      stream = null;
    },
  };
}
